<template>
  <hr-form-dialog
    ref="dialogRef"
    :title="title"
    :maximized="$q.platform.is.mobile"
    :before-dismiss="confirmDiscard"
    size="md"
    esc-dismiss
    @close="onDialogHide"
  >
    <template
      v-if="canGoBack"
      #header-leading
    >
      <hr-icon-action-btn
        icon="arrow_back"
        :aria-label="i18n.t('common.buttonBack')"
        data-e2e="button-filter-back"
        @click="onBack"
      />
    </template>

    <!--
      Leading is navigation, trailing is the reset scoped to this pane, in both panes. Clearing
      edits the draft like everything else, so it still needs Apply to take effect.
    -->
    <template #header-actions>
      <hr-subtle-btn
        v-if="canClear"
        :label="clearLabel"
        data-e2e="button-filter-clear"
        @click="onClear"
      />
    </template>

    <hr-info-banner
      v-if="dependencyMessage"
      :message="dependencyMessage"
      class="filter-dialog__banner"
    />

    <!--
      The dialog's content area is the scroller here, so the panes size to what they are showing.
      Without it a short pane, the duration editor being the shortest, scrolls inside the panel
      while the dialog still has room below it.
    -->
    <hr-panels
      :model-value="pane"
      keep-alive
      content-sized
    >
      <hr-panel name="criteria">
        <criteria-list-pane
          ref="criteriaPaneRef"
          :criteria="filterCriteria"
          :filters="draft"
          :filter-groups="filterGroups"
          :search="search"
          :selected-group="selectedGroup"
          @update:search="search = $event"
          @select="onSelectCriterion"
          @open-group="selectedGroup = $event"
        />
      </hr-panel>

      <!--
        Keyed by criterion, not just by component: Client, User and ArrayContainsUser all resolve
        to the picker, so without a key Vue patches one instance across the switch and the editor
        keeps the previous criterion's search text and never re-runs its load.
      -->
      <hr-panel name="editor">
        <!--
          Focusable so opening a criterion has somewhere to put the keyboard. Only two editors
          lead with a field of their own, and the rest would otherwise leave focus on a row that
          the pane switch has just unmounted.
        -->
        <div
          ref="editorPaneRef"
          tabindex="-1"
          class="filter-dialog__editor"
        >
          <component
            :is="editorComponent"
            v-if="selectedCriterion"
            :key="selectedCriterion.value"
            :criterion="selectedCriterion"
            :model-value="statement"
            :match-mode="matchMode"
            v-bind="editorProps"
            @update:model-value="onStatementChanged"
            @update:match-mode="onMatchModeChanged"
          />
        </div>
      </hr-panel>
    </hr-panels>

    <template #actions>
      <hr-dismiss-btn
        :label="i18n.t('common.buttonCancel')"
        data-e2e="button-filter-cancel"
        @click="onCancel"
      />
      <hr-confirm-btn
        :label="i18n.t('common.buttonApply')"
        :disable="!isDirty"
        data-e2e="button-filter-apply"
        @click="onApply"
      />
    </template>
  </hr-form-dialog>
</template>

<script setup>
import { i18n } from 'boot/i18n';
import { isEqual } from 'lodash';
import { storeToRefs } from 'pinia';
import { useDialogPluginComponent, useQuasar } from 'quasar';
import {
  computed, nextTick, onMounted, ref,
} from 'vue';

import { useDateRanges } from 'src/composables/dateRanges';
import {
  confirmDialog, HrConfirmBtn, HrDismissBtn, HrFormDialog, HrIconActionBtn, HrInfoBanner,
  HrPanel, HrPanels, HrSubtleBtn,
} from 'src/design-system';
import { clients as useClientsStore } from 'src/store/clients';
import { users as useUsersStore } from 'src/store/users';

import CriteriaListPane from './CriteriaListPane.vue';
import { getPeriodPresetOptions } from './criteriaTypes';
import { EDITOR_KINDS, getEditorKind } from './editorKinds';
import ChipsEditor from './editors/ChipsEditor.vue';
import DateEditor from './editors/DateEditor.vue';
import NumericEditor from './editors/NumericEditor.vue';
import OptionListEditor from './editors/OptionListEditor.vue';
import PickerEditor from './editors/PickerEditor.vue';
import TextEditor from './editors/TextEditor.vue';
import buildFilterLabel from './filterLabel';
import removeFilter from './filterMap';
import { buildFilter, canApplyFilter, createFilterStatement } from './filterStatement';
import { clearFilters, getMandatoryFilterKeys, isFilterRemovable } from './mandatoryFilters';
import {
  getMatchMode, getSelectedValues, isGatheredShape, MATCH_MODES, supportsMatchMode, toStatements,
} from './matchMode';

/**
 * Builds and edits every filter on a table, in one dialog.
 *
 * One draft, one commit. Opening copies the applied filters, every edit in either pane writes to
 * that copy, and nothing reaches the table until Apply. That is what keeps a control meaning the
 * same thing wherever the user is: Apply always commits everything and closes, Back only
 * navigates, and Cancel always restores exactly what was applied when the dialog opened.
 */
defineOptions({ name: 'FilterDialog' });

const props = defineProps({
  /** Every filter currently applied, keyed by criterion value. */
  filters: {
    type: Object,
    default: () => ({}),
  },
  /** Every criterion that can be filtered on. */
  filterCriteria: {
    type: Array,
    required: true,
  },
  /** Group labels the caller declared. No group structure is invented here. */
  filterGroups: {
    type: Array,
    default: () => ([]),
  },
  /** Criterion keys the caller protects, alongside any filter marked persistent. */
  lockedFilterKeys: {
    type: Array,
    default: () => ([]),
  },
  /** Opens straight onto this criterion, for editing a filter from its chip. */
  openAt: {
    type: String,
    default: null,
  },
  /** Overrides the clients the picker offers. */
  customClients: {
    type: Array,
    default: null,
  },
  /** Overrides the users the picker offers. */
  customUsers: {
    type: Array,
    default: null,
  },
  /** Replaces the dialog's own title on the criteria pane. */
  customTitle: {
    type: String,
    default: null,
  },
});

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogOK, onDialogHide } = useDialogPluginComponent();

const $q = useQuasar();

/*
 * The label a filter carries is built here, so the dialog needs what turns a stored value into
 * readable text: the names behind selected client and user ids, and the date presets behind a
 * chosen period. Asked for across every scope, since this is a lookup for labelling rather than
 * the list a criterion offers.
 */
const { clients } = storeToRefs(useClientsStore());
const { users } = storeToRefs(useUsersStore());
const { periodPresets } = useDateRanges();

const EDITORS = {
  [EDITOR_KINDS.CHIPS]: ChipsEditor,
  [EDITOR_KINDS.OPTION_LIST]: OptionListEditor,
  [EDITOR_KINDS.PICKER]: PickerEditor,
  [EDITOR_KINDS.TEXT]: TextEditor,
  [EDITOR_KINDS.NUMERIC]: NumericEditor,
  [EDITOR_KINDS.DATE_RANGE]: DateEditor,
};

const criteriaPaneRef = ref(null);
const editorPaneRef = ref(null);

const draft = ref({ ...props.filters });
const search = ref('');
const selectedGroup = ref(null);
const selectedValue = ref(props.openAt);

const mandatoryKeys = computed(() => getMandatoryFilterKeys(draft.value, props.lockedFilterKeys));

const selectedCriterion = computed(() => props.filterCriteria
  .find((criterion) => criterion.value === selectedValue.value) ?? null);

/*
 * A criterion already filtered on is edited from where it left off. Read from the draft rather
 * than the props, because within one visit the draft is what the user has built.
 *
 * A match-all filter is stored as one statement per value, so its values are gathered back into
 * the single statement the editor works with. The arrangement is remembered separately.
 */
function statementFor(criterionValue) {
  const filter = draft.value[criterionValue];
  const existing = filter?.filterStatements?.[0];
  if (!existing) {
    return createFilterStatement();
  }

  /*
   * Only a shape the editor can hold is gathered. A scalar statement, or a group structure the
   * single selection cannot express, is edited as its first statement alone and the rest is
   * preserved.
   */
  return Array.isArray(existing.filteredItems) && isGatheredShape(filter)
    ? { ...existing, filteredItems: getSelectedValues(filter) }
    : { ...existing };
}

/*
 * The statements the editor has no way to show. A scalar criterion is edited as a single
 * statement, but the filter engine reads several on one key as AND, and the cohort dialog this
 * replaces appended to whatever key was being edited without checking the criterion held a list.
 * So a saved filter can carry conditions past the first that the editor cannot represent.
 *
 * They are set aside on selection and written back untouched, because rebuilding the filter from
 * the editor alone would drop them: the filter would quietly stop meaning what it meant, and
 * return rows it had been narrowing away. Nothing writes this shape today.
 */
function preservedStatementsFor(criterionValue) {
  const filter = draft.value[criterionValue];
  const stored = filter?.filterStatements ?? [];

  // Nothing to preserve when every statement is gathered into the editor and rebuilt from it.
  return Array.isArray(stored[0]?.filteredItems) && isGatheredShape(filter)
    ? []
    : stored.slice(1);
}

/*
 * Whether picking this criterion starts a second condition or reopens the one already applied.
 *
 * Per criterion rather than per visit. A chip names the one filter it stands for, so that criterion
 * is edited — including after Back, which is how a half-finished edit survives stepping out of the
 * editor. Everything else reached from the criteria list is an addition, because that list is
 * reached by asking to add.
 *
 * Except where a second condition cannot mean anything. The engine requires every statement to
 * match, so two of them are only satisfiable when the element holds a list: a program can carry one
 * tag and not another, but a client has one gender and a session one date. Adding there produces
 * "Male AND Female", which matches nothing and reads on screen as two chips with the results stuck
 * on the first. `supportsMatchMode` already draws that line for the any/all toggle, which is the
 * same question asked about the same shapes.
 *
 * @param {Object} criterion
 * @returns {boolean}
 */
const isAddingFilterFor = (criterion) => criterion?.value !== props.openAt
  && supportsMatchMode(criterion);

const statement = ref(statementFor(props.openAt));
const matchMode = ref(getMatchMode(draft.value[props.openAt]));
const preservedStatements = ref(preservedStatementsFor(props.openAt));

const pane = computed(() => (selectedCriterion.value ? 'editor' : 'criteria'));

const editorComponent = computed(() => (selectedCriterion.value
  ? EDITORS[getEditorKind(selectedCriterion.value)]
  : null));

/* Only the picker needs the caller's record overrides; the rest read from the criterion. */
const editorProps = computed(() => (
  getEditorKind(selectedCriterion.value ?? {}) === EDITOR_KINDS.PICKER
    ? { customClients: props.customClients, customUsers: props.customUsers }
    : {}));

const groupLabel = computed(() => (search.value.trim() === ''
  ? props.filterGroups.find((group) => group.group === selectedGroup.value)?.label ?? null
  : null));

const title = computed(() => selectedCriterion.value?.label
  ?? groupLabel.value
  ?? props.customTitle
  ?? i18n.t('common.filter.dialogTitle'));

const canGoBack = computed(() => selectedCriterion.value !== null || selectedGroup.value !== null);

/*
 * A criterion can require another filter to be set first. It used to force the user into that
 * filter mid-apply; now it says which one is missing and holds this criterion out of the draft.
 */
const unmetDependency = computed(() => {
  const dependency = selectedCriterion.value?.options?.filterDependency;
  if (!dependency || dependency.isSet || draft.value[dependency.value] !== undefined) {
    return null;
  }
  return dependency;
});

const dependencyMessage = computed(() => unmetDependency.value?.bannerMessage ?? null);

/* Applying an unchanged draft would refetch a filtered set for nothing. */
const isDirty = computed(() => !isEqual(draft.value, props.filters));

/*
 * A Clear reaches only what the pane it sits on can show. Inside a group that is the group's own
 * criteria: clearing the groups beside it would empty filters the user cannot see from there, and
 * would not say it had.
 */
const clearScopeKeys = computed(() => (selectedGroup.value === null
  ? null
  : props.filterCriteria
    .filter((criterion) => criterion.group === selectedGroup.value)
    .map((criterion) => criterion.value)));

const canClear = computed(() => (selectedCriterion.value
  ? isFilterRemovable(selectedCriterion.value.value, mandatoryKeys.value)
    && draft.value[selectedCriterion.value.value] !== undefined
  : Object.keys(clearFilters(draft.value, mandatoryKeys.value, clearScopeKeys.value)).length
    < Object.keys(draft.value).length));

const clearLabel = computed(() => {
  if (selectedCriterion.value) {
    return i18n.t('common.filter.buttonClearFilter');
  }
  return clearScopeKeys.value === null
    ? i18n.t('common.filter.buttonClearAllFilters')
    : i18n.t('common.filter.buttonClearGroupFilters');
});

/*
 * Every edit lands in the draft as it happens, so there is nothing to commit per criterion and
 * Apply can mean one thing everywhere.
 *
 * A statement that cannot be applied carries no filter, which is also how a criterion is removed:
 * deselecting every value means the user has stopped filtering by it, never "match nothing".
 */
function writeThrough() {
  const criterion = selectedCriterion.value;
  if (!criterion) {
    return;
  }

  /*
   * A protected filter is not dropped by an edit that cannot be applied yet. Choosing a custom
   * date range clears the bounds until the dates are picked, and that half-built state must not
   * take the bound off an otherwise unbounded read. The applied filter stands until it is replaced.
   */
  if (!canApplyFilter(criterion, statement.value) || unmetDependency.value !== null) {
    // Protection first, so the promise above holds for its conditions as well as for the key.
    if (!isFilterRemovable(criterion.value, mandatoryKeys.value)) {
      return;
    }

    /*
     * The statement being built carries no filter yet, but the ones this visit is not editing still
     * carry theirs. Dropping the key would take an applied condition away along with a half-built
     * new one, which is the one thing adding a second condition must never do.
     */
    if (preservedStatements.value.length > 0) {
      draft.value = {
        ...draft.value,
        [criterion.value]: {
          ...draft.value[criterion.value],
          filterStatements: preservedStatements.value,
        },
      };
      return;
    }

    draft.value = removeFilter(criterion.value, draft.value);
    return;
  }

  const labelContext = {
    clients: props.customClients ?? clients.value,
    users: props.customUsers ?? users.value,
    periodPresetOptions: getPeriodPresetOptions(criterion, periodPresets.value),
  };

  /*
   * Labelled after the arrangement, not before it. Match all writes one statement per value and a
   * chip stands for a single statement, so a label built from the whole selection would put every
   * value on every chip: three tags reading "Behavior or Skillz" three times over.
   */
  const statements = toStatements(statement.value, matchMode.value)
    .map((each) => ({ ...each, label: buildFilterLabel(criterion, each, labelContext) }));

  const arranged = isAddingFilterFor(criterion)
    ? [...preservedStatements.value, ...statements]
    : [...statements, ...preservedStatements.value];

  const { filterKey, filter } = buildFilter(criterion, arranged);
  const existing = draft.value[filterKey];

  draft.value = {
    ...draft.value,
    [filterKey]: {
      ...filter,
      // Keep the id a filter already had, so editing one does not read as replacing it.
      id: existing?.id ?? filter.id,
      /*
       * isPersistent belongs to the caller, not to the criterion, so buildFilter cannot know it.
       * Dropping it on an edit unprotects the filter it marks, and immediately: the mandatory keys
       * are read off this draft, so Clear all would empty a filter that exists to bound the query.
       */
      ...(existing?.isPersistent === undefined ? {} : { isPersistent: existing.isPersistent }),
    },
  };
}

function onStatementChanged(next) {
  statement.value = next;
  writeThrough();
}

function onMatchModeChanged(next) {
  matchMode.value = next;
  writeThrough();
}

/*
 * Each step keeps the keyboard with the user: into the pane that opened, and back onto the row it
 * was opened from. Without it the row is unmounted with the pane it belongs to, focus falls back
 * to the document, and the next Tab starts again from the top of the dialog.
 *
 * The pane itself is the target rather than its first control, so an editor with no field of its
 * own is still reached. An editor that does autofocus one settles it either way: it has taken
 * focus already and the guard leaves it alone, or it takes focus a tick later and moves in from
 * the pane. On touch that field stays unfocused, which is the point of the autofocus rule.
 */
async function focusEditorPane() {
  await nextTick();
  const editorPane = editorPaneRef.value;
  if (editorPane !== null && !editorPane.contains(document.activeElement)) {
    editorPane.focus();
  }
}

/* The list is rebuilt as the pane switches back, so its rows are only reachable a tick later. */
async function focusCriterionRow(value) {
  await nextTick();
  criteriaPaneRef.value?.focusCriterion(value);
}

async function focusGroupRow(group) {
  await nextTick();
  criteriaPaneRef.value?.focusGroup(group);
}

function onSelectCriterion(criterion) {
  const stored = draft.value[criterion.value]?.filterStatements ?? [];

  selectedValue.value = criterion.value;
  const isAdding = isAddingFilterFor(criterion);

  statement.value = isAdding ? createFilterStatement() : statementFor(criterion.value);
  matchMode.value = isAdding ? MATCH_MODES.ANY : getMatchMode(draft.value[criterion.value]);
  preservedStatements.value = isAdding ? stored : preservedStatementsFor(criterion.value);
  focusEditorPane();
}

/*
 * Pure navigation: it never commits and never discards. Unwinds one level at a time, out of a
 * criterion to the list it was chosen from and only then out of a group, so the user keeps their
 * place on a long list.
 */
function onBack() {
  if (selectedCriterion.value !== null) {
    const previousValue = selectedValue.value;
    selectedValue.value = null;
    statement.value = createFilterStatement();
    matchMode.value = MATCH_MODES.ANY;
    preservedStatements.value = [];
    focusCriterionRow(previousValue);
    return;
  }

  const previousGroup = selectedGroup.value;
  selectedGroup.value = null;
  focusGroupRow(previousGroup);
}

function onClear() {
  if (selectedCriterion.value) {
    const criterionValue = selectedCriterion.value.value;

    /*
     * While adding, the editor holds the new condition and the applied ones are off screen.
     * Clearing what is on screen must not take away what is not, so this discards the condition
     * being built and leaves the rest standing — and it is the only way to abandon one, since Back
     * keeps whatever has already been written through. Removing the filter outright is the chip's
     * own cross, where every condition it would take is visible.
     */
    if (isAddingFilterFor(selectedCriterion.value) && preservedStatements.value.length > 0) {
      draft.value = {
        ...draft.value,
        [criterionValue]: {
          ...draft.value[criterionValue],
          filterStatements: preservedStatements.value,
        },
      };
      onBack();
      return;
    }

    draft.value = removeFilter(criterionValue, draft.value);
    onBack();
    return;
  }

  draft.value = clearFilters(draft.value, mandatoryKeys.value, clearScopeKeys.value);
}

function onApply() {
  onDialogOK(draft.value);
}

/*
 * A session can hold several criteria, so a reflexive dismissal would lose all of them. An
 * unchanged draft goes without comment.
 *
 * Both buttons name what they do, rather than a Cancel that means the opposite of the Cancel just
 * pressed. The wording is "applied" throughout, since that is what this dialog does to a table;
 * nothing here is saved.
 */
/*
 * The prompt takes a moment to appear, so a second Cancel in that window would ask again over a
 * dialog the user has already been asked about. Escape needs no such guard — it comes through the
 * dialog's escape handling, and the prompt is the topmost layer once it is open.
 */
const isConfirmingDiscard = ref(false);

function confirmDiscard() {
  if (!isDirty.value) {
    return true;
  }

  if (isConfirmingDiscard.value) {
    return false;
  }
  isConfirmingDiscard.value = true;

  return new Promise((resolve) => {
    const settle = (discard) => {
      isConfirmingDiscard.value = false;
      resolve(discard);
    };

    confirmDialog(
      i18n.t('common.filter.titleDiscardChanges'),
      i18n.t('common.filter.textDiscardChanges'),
      () => settle(true),
      {
        confirmLabel: i18n.t('common.buttonDiscard'),
        cancelLabel: i18n.t('common.buttonKeepEditing'),
      },
      () => settle(false),
    );
  });
}

async function onCancel() {
  if (await confirmDiscard()) {
    onDialogHide();
  }
}

// Opened from a chip, so the dialog starts on the editor and the same focus rule applies to it.
onMounted(() => {
  if (selectedCriterion.value !== null) {
    focusEditorPane();
  }
});

defineExpose({ show: () => dialogRef.value?.show(), hide: () => dialogRef.value?.hide() });
</script>

<style lang="scss" scoped>
.filter-dialog__banner {
  margin-bottom: 16px;
}

/*
 * The pane is a focus target, not a control: it takes focus so the next Tab carries on from inside
 * the editor, and ringing the whole pane would read as though the pane itself were selectable.
 */
.filter-dialog__editor:focus {
  outline: none;
}
</style>
